import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import { PROPERTIES } from '@/data/index';
import { ROUTE_PATHS, type ContactSubmission } from '@/lib/index';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, Eye, Star, Mail, Plus, Settings, ArrowRight } from 'lucide-react';

export default function AdminDashboard() {
  const [contactCount, setContactCount] = useState(0);
  const [recentContacts, setRecentContacts] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);

  const totalProperties = PROPERTIES.length;
  const publishedProperties = PROPERTIES.filter(p => p.status === 'Available').length;
  const featuredProperties = PROPERTIES.filter(p => p.featured).length;

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        const { data: countData, error: countError } = await supabase
          .from('contact_submissions_ave')
          .select('*', { count: 'exact', head: true });

        if (!countError && countData !== null) {
          setContactCount(countData as unknown as number);
        }

        const { data: recentData, error: recentError } = await supabase
          .from('contact_submissions_ave')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(5);

        if (!recentError && recentData) {
          setRecentContacts(recentData as ContactSubmission[]);
        }
      } catch (error) {
        console.error('Failed to fetch contact data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContactData();
  }, []);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-2">Welcome to Anchor Vault Estate Admin</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Properties</CardTitle>
              <Building2 className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{totalProperties}</div>
              <p className="text-xs text-muted-foreground mt-1">All property listings</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Published</CardTitle>
              <Eye className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{publishedProperties}</div>
              <p className="text-xs text-muted-foreground mt-1">Available properties</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Featured</CardTitle>
              <Star className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{featuredProperties}</div>
              <p className="text-xs text-muted-foreground mt-1">Featured listings</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Contact Inquiries</CardTitle>
              <Mail className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{loading ? '...' : contactCount}</div>
              <p className="text-xs text-muted-foreground mt-1">Total submissions</p>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-foreground">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to={ROUTE_PATHS.ADMIN_PROPERTIES_NEW}>
                <Button className="w-full h-auto py-6 flex flex-col items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground">
                  <Plus className="h-6 w-6" />
                  <span className="font-medium">Add New Property</span>
                </Button>
              </Link>

              <Link to={ROUTE_PATHS.ADMIN_PROPERTIES}>
                <Button variant="outline" className="w-full h-auto py-6 flex flex-col items-center gap-2 border-border hover:bg-muted">
                  <Building2 className="h-6 w-6" />
                  <span className="font-medium">View All Properties</span>
                </Button>
              </Link>

              <Link to={ROUTE_PATHS.ADMIN_SETTINGS}>
                <Button variant="outline" className="w-full h-auto py-6 flex flex-col items-center gap-2 border-border hover:bg-muted">
                  <Settings className="h-6 w-6" />
                  <span className="font-medium">Site Settings</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-foreground">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading recent contacts...</div>
            ) : recentContacts.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No contact submissions yet</p>
                <p className="text-sm text-muted-foreground mt-2">Contact inquiries will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentContacts.map((contact) => (
                  <div key={contact.id} className="flex items-start justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-medium text-foreground">{contact.name}</h4>
                        <span className="text-xs px-2 py-1 rounded-full bg-accent/10 text-accent border border-accent/20">
                          {contact.interest}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{contact.email}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(contact.created_at)}</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}